// Office / LAN project launcher — open inside this hub (iframe), not a new tab.
import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import "./OfficeProjects.css";

const STORAGE_KEY = "wdOfficeProjects.v1";

const DEFAULT_PROJECTS = [
  {
    id: "seed-238-8000",
    name: "Office server :8000",
    url: "http://192.168.100.238:8000/",
    notes: "LAN / office local deploy",
  },
];

function loadProjects() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PROJECTS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_PROJECTS;
    return parsed;
  } catch {
    return DEFAULT_PROJECTS;
  }
}

function saveProjects(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function normalizeUrl(input) {
  const trimmed = String(input || "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `http://${trimmed}`;
}

function isValidHttpUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const EMPTY_FORM = { name: "", url: "", notes: "" };

export default function OfficeProjects() {
  const [projects, setProjects] = useState(() => loadProjects());
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [active, setActive] = useState(null);
  const [frameKey, setFrameKey] = useState(0);

  useEffect(() => {
    saveProjects(projects);
  }, [projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.url.toLowerCase().includes(q) ||
        String(p.notes || "")
          .toLowerCase()
          .includes(q)
    );
  }, [projects, query]);

  function showToast(type, message) {
    setToast({ type, message });
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setError("");
  }

  function startEdit(project) {
    setEditId(project.id);
    setForm({
      name: project.name,
      url: project.url,
      notes: project.notes || "",
    });
    setError("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    const name = form.name.trim();
    const url = normalizeUrl(form.url);
    const notes = form.notes.trim();

    if (!name) {
      setError("Project name is required.");
      return;
    }
    if (!isValidHttpUrl(url)) {
      setError("Enter a valid http(s) URL, e.g. http://192.168.100.238:8000/");
      return;
    }

    if (editId) {
      const next = projects.map((p) =>
        p.id === editId ? { ...p, name, url, notes } : p
      );
      setProjects(next);
      if (active?.id === editId) {
        setActive({ id: editId, name, url, notes });
        setFrameKey((k) => k + 1);
      }
      showToast("success", "Project updated.");
    } else {
      const created = {
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name,
        url,
        notes,
      };
      setProjects((prev) => [created, ...prev]);
      showToast("success", "Project added.");
    }
    resetForm();
  }

  function handleDelete(id) {
    const target = projects.find((p) => p.id === id);
    if (!target) return;
    if (!window.confirm(`Remove “${target.name}” from this list?`)) return;
    setProjects((prev) => prev.filter((p) => p.id !== id));
    if (editId === id) resetForm();
    if (active?.id === id) setActive(null);
    showToast("info", "Project removed.");
  }

  function openInside(project) {
    setActive(project);
    setFrameKey((k) => k + 1);
  }

  function closeViewer() {
    setActive(null);
  }

  function reloadFrame() {
    setFrameKey((k) => k + 1);
  }

  if (active) {
    return (
      <MainLayout>
        <div className="office-projects-page office-viewer-mode">
          <div className="office-viewer-bar">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeViewer}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />
              Projects
            </button>
            <div className="office-viewer-meta">
              <strong>{active.name}</strong>
              <span className="office-url small text-muted" title={active.url}>
                {active.url}
              </span>
            </div>
            <div className="office-viewer-actions">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={reloadFrame}>
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
                Reload
              </button>
            </div>
          </div>
          <p className="office-viewer-hint small text-muted mb-2">
            Opens inside this hub. If the frame stays blank, that site blocks embedding (X-Frame-Options / CSP).
          </p>
          <div className="office-frame-wrap">
            <iframe
              key={frameKey}
              title={active.name}
              src={active.url}
              className="office-frame"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="office-projects-page">
        <div className="page-header mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="header-icon" aria-hidden="true">
              <i className="bi bi-hdd-network" />
            </div>
            <div>
              <h4 className="mb-0 fw-bold">Office Local Projects</h4>
              <p className="text-muted mb-0 small">
                Multiple LAN URLs — Open loads the site inside this page (not a new browser tab).
              </p>
            </div>
          </div>
        </div>

        {toast ? (
          <div
            className={`alert alert-${toast.type === "success" ? "success" : "info"} alert-dismissible fade show`}
            role="alert"
          >
            {toast.message}
            <button
              type="button"
              className="btn-close"
              aria-label="Dismiss"
              onClick={() => setToast(null)}
            />
          </div>
        ) : null}

        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-white border-bottom px-4 py-3">
            <h5 className="mb-0 fw-bold">{editId ? "Edit project" : "Add project"}</h5>
          </div>
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold" htmlFor="op-name">
                    Name
                  </label>
                  <input
                    id="op-name"
                    className="form-control"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Billing API"
                    autoComplete="off"
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label fw-semibold" htmlFor="op-url">
                    URL
                  </label>
                  <input
                    id="op-url"
                    className="form-control"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="http://192.168.100.238:8000/"
                    autoComplete="off"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" htmlFor="op-notes">
                    Notes
                  </label>
                  <input
                    id="op-notes"
                    className="form-control"
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                </div>
              </div>
              {error ? (
                <div className="text-danger small mt-2" role="alert">
                  {error}
                </div>
              ) : null}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <button type="submit" className="btn btn-primary">
                  <i className={`bi ${editId ? "bi-check-lg" : "bi-plus-lg"} me-1`} aria-hidden="true" />
                  {editId ? "Save changes" : "Add project"}
                </button>
                {editId ? (
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom px-4 py-3 d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold">Projects ({filtered.length})</h5>
            <div className="office-search">
              <label className="visually-hidden" htmlFor="op-search">
                Search projects
              </label>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search" aria-hidden="true" />
                </span>
                <input
                  id="op-search"
                  className="form-control"
                  type="search"
                  placeholder="Search name, URL, notes…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {filtered.length === 0 ? (
              <div className="office-empty text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2" aria-hidden="true" />
                <div className="fw-semibold">No projects match</div>
                <div className="small mt-1">Add a URL above or clear the search.</div>
              </div>
            ) : (
              <ul className="office-list list-unstyled mb-0">
                {filtered.map((p) => (
                  <li key={p.id} className="office-item">
                    <div className="office-item-main">
                      <div className="fw-bold">{p.name}</div>
                      <button
                        type="button"
                        className="btn btn-link office-url small p-0 text-start"
                        onClick={() => openInside(p)}
                      >
                        {p.url}
                      </button>
                      {p.notes ? <div className="text-muted small mt-1">{p.notes}</div> : null}
                    </div>
                    <div className="office-item-actions">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => openInside(p)}
                      >
                        <i className="bi bi-window me-1" aria-hidden="true" />
                        Open here
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => startEdit(p)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(p.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-muted small mt-3 mb-0">
              Opens inside this hub page. Your PC must reach the LAN URL (office network / VPN).
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
